import { getIdentifiedPlatformConfig } from '@hmhco/login-config';

/**
 * Maps an API school (e.g. { schoolId: '7abc...', name: 'ALLCO', address: { city: 'KINGWOOD', zip: '00000' } })
 * to the dropdown option shape.
 */
export const mapSchool = ({ schoolId, name, address }) => ({
  id: schoolId,
  name,
  addressCity: address?.city,
  addressStreet: address?.street,
  addressZip: address?.zip,
});

/**
 * Fetches the schools belonging to a district from the HCP Identity API Gateway.
 *
 * Sample usage:
 * fetchDistrictSchools({ orgRefId: 'eddabbd0-8776-46ad-842d-af796df23a4a' });
 *
 * @param {Object} params
 * @param {string} params.orgRefId - District org reference ID
 * @returns {Promise<Array<{id: string, name: string, addressCity: string, addressStreet: string, addressZip: string}>>}
 * Schools mapped to the shape used by the school dropdowns
 * @throws {Error} When orgRefId or app config is missing, or the request fails
 */
export const fetchDistrictSchools = async ({ orgRefId }) => {
  if (!orgRefId) {
    throw new Error(
      'Missing orgRefId: a district org reference ID is required',
    );
  }

  const { hcpIdentityDistrictSchoolsApi } = getIdentifiedPlatformConfig();

  if (!hcpIdentityDistrictSchoolsApi) {
    throw new Error('Missing app config: hcpIdentityDistrictSchoolsApi');
  }

  const response = await fetch(
    hcpIdentityDistrictSchoolsApi.replace(
      '{orgRefId}',
      encodeURIComponent(orgRefId),
    ),
    { headers: { Accept: 'application/json', source: 'HCP' } },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch district schools: ${response.status} ${response.statusText}`,
    );
  }

  const schools = await response.json();

  return (schools || []).map(mapSchool);
};

export default fetchDistrictSchools;
